import React, { useState, useEffect } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Button, FormControl } from 'react-bootstrap';
import { trackPageView } from '../util/Analytics.jsx';
import Utils from '../util/Utils.jsx';

import { useStateValue } from '../context/state.jsx';
import { openAlert } from '../context/alert.js';
import { setTokenExpired } from '../context/token.js';
import { setLoginSucceeded } from '../context/loginSucceeded.js';

const parseInviteParams = (location) => {
	let email = '';
	let token = '';

	const search = (location && location.search) ||
		(typeof window !== 'undefined' ? window.location.search : '');
	if (search) {
		try {
			const params = new URLSearchParams(search);
			email = params.get('email') || '';
			token = params.get('token') || params.get('inviteToken') || '';
		} catch (e) {
			// ignore malformed query strings
		}
	}

	return { email: email.trim(), token: token.trim() };
};

const SignUpPage = () => {
	const location = useLocation();
	const [, dispatch] = useStateValue();
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [password, setPassword] = useState('');
	const [repeatPassword, setRepeatPassword] = useState('');
	const [email, setEmail] = useState('');
	const [inviteToken, setInviteToken] = useState('');
	const [isInvited, setIsInvited] = useState(false);
	const [inviteIncomplete, setInviteIncomplete] = useState(false);
	const [redirectTo, setRedirectTo] = useState(null);

	useEffect(() => {
		trackPageView();

		const { email: inviteEmail, token } = parseInviteParams(location);

		if (inviteEmail) {
			setEmail(inviteEmail);
			setIsInvited(true);
			if (token) {
				setInviteToken(token);
				setInviteIncomplete(false);
			} else {
				setInviteToken('');
				setInviteIncomplete(true);
				dispatch(openAlert(
					'Incomplete invitation',
					'This invitation link is missing a security token. Please use the link from your invitation email, or ask the project owner to send a new invite.'
				));
			}
			return;
		}

		// Legacy links with token only (no email param): keep invite submit path, email editable until filled by user is not needed — server resolves email from token.
		if (token) {
			setInviteToken(token);
			setIsInvited(true);
			setInviteIncomplete(false);
		}
	}, []);

	const lockEmail = isInvited && Boolean(email);

	const isEmailValid = value => {
		//http://stackoverflow.com/questions/46155/validate-email-address-in-javascript
		const re = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/i;//this quote is only to fix syntax highlighting'
		return re.test(value);
	};

	const formSubmit = e => {
		e.preventDefault();

		if (isInvited && inviteIncomplete) {
			dispatch(openAlert(
				'Incomplete invitation',
				'This invitation link is missing a security token. Please use the link from your invitation email, or ask the project owner to send a new invite.'
			));
			return;
		}

		const postData = {
			signupFirstName: firstName,
			signupLastName: lastName,
			signupPassword: password,
			signupRepeatPassword: repeatPassword,
			signupEmail: email
		};

		if (isInvited && inviteToken) {
			postData.inviteToken = inviteToken;
		}

		if (!postData.signupFirstName ||
			!postData.signupLastName ||
			!postData.signupPassword ||
			!postData.signupRepeatPassword ||
			(!postData.signupEmail && !(isInvited && inviteToken))) {
			dispatch(openAlert(
				'All fields are required',
				'You must enter a name, email address, password, and repeat your password.'
			));
			return;
		}

		if (postData.signupPassword !== postData.signupRepeatPassword) {
			dispatch(openAlert('Password mismatch', 'Your passwords must match.'));
			return false;
		}
		if (postData.signupEmail && !isEmailValid(postData.signupEmail)) {
			dispatch(openAlert('Invalid email', 'Please enter a valid email address.'));
			return false;
		}
		if (isInvited && !inviteToken) {
			dispatch(openAlert(
				'Incomplete invitation',
				'This invitation link is missing a security token. Please use the link from your invitation email.'
			));
			return;
		}

		Utils.ajax(
			'signup',
			postData,
			dispatch,
			result => {
				if (!result || !result.success) {
					return;
				}
				if (isInvited && result.Token && result.datasetId) {
					Utils.allowAuth();
					localStorage.setItem('token', result.Token);
					dispatch(setTokenExpired(false));
					dispatch(setLoginSucceeded(true));
					setRedirectTo(`/app/dataset/${result.datasetId}`);
					return;
				}
				dispatch(openAlert('Check your email', 'Please check your email to verify your email address.'));
			}
		);
	};

	const firstNameChange = e => {
		setFirstName(e.target.value);
	};

	const lastNameChange = e => {
		setLastName(e.target.value);
	};

	const passwordChange = e => {
		setPassword(e.target.value);
	};

	const repeatPasswordChange = e => {
		setRepeatPassword(e.target.value);
	};

	const emailChange = e => {
		if (lockEmail) return;
		setEmail(e.target.value);
	};

	if (redirectTo) {
		return (<Navigate to={redirectTo} replace />);
	}

	return (
		<div className="signUpPage">
			<div className="pageContent">
				<div>
					<form className="signUpForm" onSubmit={formSubmit}>
						<h2>{isInvited ? 'Complete Your Invitation' : 'Create Your SiteWalker Account'}</h2>
						{lockEmail && (
							<p className="inviteHint">
								Completing your invitation for <strong>{email}</strong>
							</p>
						)}
						<div>
							<FormControl className="firstName" type="text" placeholder="First name" onChange={firstNameChange} value={firstName} />
							<FormControl className="lastName" type="text" placeholder="Last name" onChange={lastNameChange} value={lastName} />
						</div>
						<div>
							<FormControl
								className={lockEmail ? 'inviteEmailLocked' : ''}
								type="email"
								placeholder="Email"
								onChange={emailChange}
								value={email}
								readOnly={lockEmail}
								disabled={lockEmail}
							/>
						</div>
						<div>
							<FormControl type="password" placeholder="Password" onChange={passwordChange} value={password} />
						</div>
						<div>
							<FormControl type="password" placeholder="Repeat Password" onChange={repeatPasswordChange} value={repeatPassword} />
						</div>
						<div className="agreement">
							By signing up, you agree to the <a className="underline blueLink" href="https://get.sitewalkerapp.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
						</div>
						<div>
							<Button
								block
								bsStyle="primary"
								onClick={formSubmit}
								disabled={isInvited && inviteIncomplete}
							>
								{isInvited ? 'Join Project' : 'Create My Account'}
							</Button>
						</div>
						<div>
							<Link to='/signin'>
								<Button block>
									<span className="glyphicon glyphicon-arrow-left leftGlyph"></span>
									Have a login? Sign in!
								</Button>
							</Link>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};

export default SignUpPage;
